import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  type Icon,
  ListBullets,
  MagnifyingGlass,
  SquaresFour,
  X,
} from "@phosphor-icons/react";
import { Button } from "./Button";
import { cn } from "./cn";

/**
 * - `tabs` (default): title with controls and action on one row, underline
 *   tabs beneath. For list pages that switch between sections.
 * - `classic`: title, then solid filter pills with the controls. For
 *   narrowing a collection.
 */
export type PageHeaderVariant = "tabs" | "classic";

export interface PageHeaderTab {
  value: string;
  label: string;
  /** Shown beside the label, e.g. trends in that topic. */
  count?: number;
}

interface PageHeaderProps {
  /** Hide the title block and action: for a section that sits under a page header of its own. */
  bare?: boolean;
  title: string;
  description?: string;
  /** The page's primary action, top right. */
  action?: ReactNode;
  /** Tabs (or filter pills, in `classic`). */
  tabs?: {
    items: PageHeaderTab[];
    value: string;
    onChange: (value: string) => void;
    label: string;
  };
  /** A button at the end of the tabs, e.g. add or customize. */
  trailing?: ReactNode;
  /** Controls: search, filters, view toggle. */
  controls?: ReactNode;
  /** A line under the header: result count, active filters. */
  summary?: ReactNode;
  variant?: PageHeaderVariant;
}

/**
 * App-wide page header: title, description and primary action; tabs with a
 * trailing button; controls; a summary line. Every part but the title is
 * optional.
 */
export function PageHeader({ variant = "tabs", ...props }: PageHeaderProps) {
  return variant === "classic" ? (
    <ClassicHeader {...props} />
  ) : (
    <TabsHeader {...props} />
  );
}

type Props = Omit<PageHeaderProps, "variant">;

/* ── Shared pieces ── */

function TitleBlock({
  title,
  description,
}: Pick<Props, "title" | "description">) {
  return (
    <div className="min-w-0">
      <h1 className="text-2xl font-bold tracking-[-0.02em] sm:text-3xl">
        {title}
      </h1>
      {description && (
        <p className="mt-1.5 max-w-2xl text-sm text-muted-foreground sm:text-base">
          {description}
        </p>
      )}
    </div>
  );
}

const Count = ({ n, active }: { n?: number; active?: boolean }) =>
  n === undefined ? null : (
    <span
      className={cn(
        "ml-1.5 text-xs tabular-nums",
        active ? "opacity-70" : "text-muted-foreground/70"
      )}
    >
      {n}
    </span>
  );

/**
 * Tabs whose highlight slides to the active one: an underline (`tabs`) or a
 * solid pill (`classic`). They wrap rather than scroll — a hidden overflow is
 * one most people never find — and `trailing` wraps along as the last item.
 */
function SlidingTabs({
  tabs,
  look,
  trailing,
}: {
  tabs: NonNullable<Props["tabs"]>;
  look: "pill" | "underline";
  trailing?: ReactNode;
}) {
  const id = useId();
  const reduce = useReducedMotion();
  const transition = reduce
    ? { duration: 0 }
    : { type: "spring" as const, bounce: 0.15, duration: 0.45 };

  return (
    <div
      role="tablist"
      aria-label={tabs.label}
      className={cn(
        "flex min-w-0 flex-wrap items-center",
        look === "pill" ? "gap-1" : "gap-x-5"
      )}
    >
      {tabs.items.map((tab) => {
        const active = tab.value === tabs.value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => tabs.onChange(tab.value)}
            className={cn(
              "relative shrink-0 whitespace-nowrap text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              look === "underline"
                ? "rounded-sm pb-3 pt-1"
                : "rounded-full px-3.5 py-1.5",
              active
                ? look === "pill"
                  ? "font-medium text-background"
                  : "font-medium text-foreground"
                : "text-muted-foreground hover:text-foreground",
              // Tabs with nothing in them read quieter.
              !active && tab.count === 0 && "opacity-60"
            )}
          >
            {active && (
              <motion.span
                layoutId={`${id}-highlight`}
                transition={transition}
                className={cn(
                  "absolute",
                  look === "pill"
                    ? "inset-0 rounded-full bg-foreground"
                    : "inset-x-0 -bottom-px h-0.5 rounded-full bg-primary"
                )}
                aria-hidden
              />
            )}
            <span className="relative">
              {tab.label}
              <Count n={tab.count} active={active} />
            </span>
          </button>
        );
      })}
      {trailing && (
        <div className={cn("shrink-0", look === "underline" && "pb-1.5")}>
          {trailing}
        </div>
      )}
    </div>
  );
}

const Summary = ({ children }: { children?: ReactNode }) =>
  children ? (
    <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
      {children}
    </div>
  ) : null;

/* ── Variants ── */

/** Title with controls and action on one row; underline tabs beneath. */
function TabsHeader({
  title,
  description,
  action,
  tabs,
  trailing,
  controls,
  summary,
}: Props) {
  return (
    <header className="space-y-[calc(var(--space-section)*0.667)]">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <TitleBlock title={title} description={description} />
        {(controls || action) && (
          <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-end">
            {controls && (
              <div className="flex items-center gap-1">{controls}</div>
            )}
            {action}
          </div>
        )}
      </div>
      {(tabs || trailing) && (
        <div className="border-b border-border">
          {tabs ? (
            <SlidingTabs tabs={tabs} look="underline" trailing={trailing} />
          ) : (
            <div className="pb-1.5">{trailing}</div>
          )}
        </div>
      )}
      <Summary>{summary}</Summary>
    </header>
  );
}

/** Title, then solid filter pills with the controls beside them. */
function ClassicHeader({
  bare,
  title,
  description,
  action,
  tabs,
  trailing,
  controls,
  summary,
}: Props) {
  return (
    <header className="space-y-[calc(var(--space-section)*0.833)]">
      {!bare && (
        <div className="flex flex-wrap items-start justify-between gap-4">
          <TitleBlock title={title} description={description} />
          {action && <div className="shrink-0">{action}</div>}
        </div>
      )}
      {(tabs || trailing || controls || (bare && action)) && (
        // Below lg the pills get the full width and the controls drop to
        // their own row; side by side, the controls squeezed the pills out.
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1">
            {tabs ? (
              <SlidingTabs tabs={tabs} look="pill" trailing={trailing} />
            ) : (
              trailing
            )}
          </div>
          {(controls || (bare && action)) && (
            <div className="flex w-full items-center justify-between gap-2 lg:w-auto lg:justify-start">
              {controls}
              {bare && action}
            </div>
          )}
        </div>
      )}
      <Summary>{summary}</Summary>
    </header>
  );
}

/* ── Controls ── */

/**
 * A page's primary action in the header's action slot: the round, small
 * primary button the Posts page set the look with. Every page's primary
 * action goes through this, so they all read the same.
 */
export function HeaderAction({
  icon,
  onClick,
  loading,
  disabled,
  title,
  "aria-label": ariaLabel,
  children,
}: {
  icon?: ReactNode;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  title?: string;
  /** Names the button when the label collapses; defaults to the label text. */
  "aria-label"?: string;
  /** The label; it collapses to the icon on a phone. */
  children: ReactNode;
}) {
  return (
    <Button
      size="sm"
      pill
      onClick={onClick}
      loading={loading}
      disabled={disabled}
      title={title}
      aria-label={
        ariaLabel ?? (typeof children === "string" ? children : undefined)
      }
      className="shrink-0 gap-1.5 whitespace-nowrap"
    >
      {icon}
      <span className={icon ? "hidden sm:inline" : undefined}>{children}</span>
    </Button>
  );
}

/** Round icon button for the header's controls. */
export function HeaderIconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {children}
    </button>
  );
}

/** A dashed round "add" button for the end of the tabs (e.g. customize). */
export function HeaderAddButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-8 w-8 items-center justify-center rounded-full border border-dashed border-border text-muted-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {children}
    </button>
  );
}

const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

/**
 * Search that starts as an icon and opens into a field. "/" opens it from
 * anywhere on the page; Escape or × clears and closes it.
 */
export function HeaderSearch({
  value,
  onChange,
  placeholder = "Search",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(value.length > 0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || isTyping(e.target)) return;
      e.preventDefault();
      setOpen(true);
      inputRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const close = () => {
    onChange("");
    setOpen(false);
  };

  if (!open) {
    return (
      <HeaderIconButton
        label={`${placeholder} ( / )`}
        onClick={() => setOpen(true)}
      >
        <MagnifyingGlass className="h-4.5 w-4.5" />
      </HeaderIconButton>
    );
  }

  return (
    <div className="relative">
      <MagnifyingGlass
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === "Escape" && close()}
        onBlur={() => !value && setOpen(false)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="h-9 w-44 rounded-full border border-input bg-background pl-9 pr-8 text-sm placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 sm:w-56 [&::-webkit-search-cancel-button]:hidden"
      />
      <button
        type="button"
        onClick={close}
        aria-label="Clear search"
        className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" weight="bold" />
      </button>
    </div>
  );
}

export type ViewMode = "grid" | "list";

export interface ViewOption<T extends string> {
  value: T;
  label: string;
  icon: Icon;
}

const GRID_LIST: ViewOption<ViewMode>[] = [
  { value: "grid", label: "Grid view", icon: SquaresFour },
  { value: "list", label: "List view", icon: ListBullets },
];

/**
 * Switch between a page's views: grid/list by default, or any options
 * (e.g. list/calendar on Posts). Icon-only unless `showLabels`.
 */
export function ViewToggle<T extends string = ViewMode>({
  value,
  onChange,
  options = GRID_LIST as unknown as ViewOption<T>[],
  showLabels = false,
}: {
  value: T;
  onChange: (value: T) => void;
  options?: ViewOption<T>[];
  showLabels?: boolean;
}) {
  return (
    <div
      className="flex items-center gap-0.5 rounded-full bg-muted p-1"
      role="group"
      aria-label="View"
    >
      {options.map(({ value: option, label, icon: OptionIcon }) => {
        const active = value === option;
        return (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            aria-pressed={active}
            aria-label={label}
            title={label}
            className={cn(
              "flex h-7 items-center justify-center gap-1.5 rounded-full text-sm transition-colors",
              showLabels ? "px-3" : "w-7",
              active
                ? "bg-card font-medium text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <OptionIcon
              className="h-4 w-4"
              weight={active ? "fill" : "regular"}
            />
            {showLabels && <span className="hidden sm:inline">{label}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** A removable chip for an active filter, for the summary line. */
export function FilterChip({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card py-0.5 pl-2.5 pr-1 text-xs text-foreground">
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove filter: ${label}`}
        className="flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <X className="h-3 w-3" weight="bold" />
      </button>
    </span>
  );
}
